import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isExpertiseOpen, setIsExpertiseOpen] = useState(false);
  const [dropdownTimeout, setDropdownTimeout] = useState<NodeJS.Timeout | null>(null);
  const location = useLocation();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout);
      }
    };
  }, [dropdownTimeout]);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about' },
    { 
      name: 'Our Expertise', 
      href: '#',
      isDropdown: true,
      subItems: [
        { name: 'Event Management', href: '/event-management' },
        { name: 'Audio Visual Production', href: '/av-production' },
        { name: 'Advertisements', href: '/advertisements' }
      ]
    },
    { name: 'Milestone Projects', href: '/milestone-projects' },
    { name: 'Contact Us', href: '/contact' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="fixed top-0 w-full z-50 bg-white backdrop-blur-sm border-b border-neutral-200">
      <nav className="container-grid items-center py-4">
        <div className="col-span-12 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/company-logo.jpg" 
              alt="Mirchi Republic Media Production Logo" 
              className="h-16 w-auto object-contain hover:opacity-90 transition-opacity duration-200 logo-color-preserve"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => (
              <div key={item.name} className="relative">
                {item.isDropdown ? (
                  <div 
                    className="relative"
                    onMouseEnter={() => {
                      if (dropdownTimeout) {
                        clearTimeout(dropdownTimeout);
                        setDropdownTimeout(null);
                      }
                      setIsExpertiseOpen(true);
                    }}
                    onMouseLeave={() => {
                      const timeout = setTimeout(() => {
                        setIsExpertiseOpen(false);
                      }, 150);
                      setDropdownTimeout(timeout);
                    }}
                  >
                    <button
                      className="font-medium transition-colors duration-200 flex items-center space-x-1 text-neutral-700 hover:text-brand-1"
                      onClick={() => setIsExpertiseOpen(!isExpertiseOpen)}
                    >
                      <span>{item.name}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpertiseOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Dropdown Menu */}
                    {isExpertiseOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute top-full left-0 mt-1 w-60 bg-white border border-neutral-200 rounded-lg shadow-lg py-2 z-50"
                        onMouseEnter={() => {
                          if (dropdownTimeout) {
                            clearTimeout(dropdownTimeout);
                            setDropdownTimeout(null);
                          }
                        }}
                        onMouseLeave={() => {
                          const timeout = setTimeout(() => {
                            setIsExpertiseOpen(false);
                          }, 150);
                          setDropdownTimeout(timeout);
                        }}
                      >
                        {item.subItems?.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className="block px-4 py-3 hover:bg-neutral-50 transition-colors duration-200 cursor-pointer"
                            onClick={() => {
                              setIsExpertiseOpen(false);
                            }}
                          >
                            <div className="font-medium text-neutral-900">{subItem.name}</div>
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`font-medium transition-colors duration-200 relative ${
                      isActive(item.href)
                        ? 'text-brand-1'
                        : 'text-neutral-700 hover:text-brand-1'
                    }`}
                  >
                    {item.name}
                    {isActive(item.href) && (
                      <motion.div
                        className="absolute -bottom-1 left-0 w-full h-0.5 bg-brand-1"
                        layoutId="activeTab"
                      />
                    )}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-md hover:bg-neutral-200 text-neutral-700"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden col-span-12 mt-4 pb-4 border-t border-neutral-200"
          >
            <div className="flex flex-col space-y-4 pt-4">
              {navigation.map((item) => (
                <div key={item.name}>
                  {item.isDropdown ? (
                    <div>
                      <button
                        onClick={() => setIsExpertiseOpen(!isExpertiseOpen)}
                        className="font-medium py-2 text-neutral-700 flex items-center justify-between w-full"
                      >
                        <span>{item.name}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpertiseOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isExpertiseOpen && (
                        <div className="pl-4 space-y-2 mt-2">
                          {item.subItems?.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              onClick={() => {
                                setIsMenuOpen(false);
                                setIsExpertiseOpen(false);
                              }}
                              className="block py-2 text-sm text-neutral-600 hover:text-brand-1"
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={() => setIsMenuOpen(false)}
                      className={`font-medium py-2 block ${
                        isActive(item.href)
                          ? 'text-brand-1'
                          : 'text-neutral-700'
                      }`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </nav>
    </header>
  );
};

export default Header;